<script setup lang="ts">
/**
 * @author Beka Chkhaidze
 */

import { computed, onBeforeMount } from "vue";
import { useEventListener } from "@/composables/utils/events";
import { useGoogleMap } from "./ts/googleMap";
import { useMainStore } from "@/store/main/index";


import type { SecondaryDataInterface } from "@/types/ContentType";

const props = defineProps<{
  contentData?: SecondaryDataInterface;
}>();

const mainStore = useMainStore();
const isMobXs = computed(() => mainStore.windowWidth < mainStore.breakpoints.mobile_sm);

const {
  mapCenter,
  mapOptions,
  mapLoaded,
  MAP_ZOOM_LEVEL,
  MAP_PIN_SVG,
  pixelOffsets,
  attachMapComponent,
  handleKeyDown,
  handleKeyUp,
  onMarkerClick,
} = useGoogleMap(props.contentData);

useEventListener(window, "keydown", handleKeyDown);
useEventListener(window, "keyup", handleKeyUp);

onBeforeMount(() => {
  attachMapComponent();
});
</script>

<template>
  <section class="google-map c-white" v-if="mapLoaded">
    <GMapMap
      class="google-map__map"
      map-type-id="terrain"
      :center="mapCenter"
      :zoom="MAP_ZOOM_LEVEL"
      :options="mapOptions"
    >
      <template v-if="contentData?.data?.listCount">
        <GMapMarker
          v-for="(marker, index) in contentData?.data?.list"
          :key="index"
          :position="{
            lat: Number(marker.location?.lat),
            lng: Number(marker.location?.lng),
          }"
          :draggable="false"
          :icon="{
            url: MAP_PIN_SVG,
            scaledSize: { width: 48, height: 48 },
            labelOrigin: { x: 16, y: -10 },
          }"
          @click="onMarkerClick(marker as any)"
          class="google-map__marker"
          :title="marker?.url?.title"
        >
          <GMapInfoWindow
            class="google-map__info"
            :aria-label="marker?.title"
            :position="{
              lat: Number(marker?.location?.lat) - 100,
              lng: Number(marker?.location?.lng),
            }"
            :opened="true"
            v-bind="{
              options: {
                pixelOffset: index % 2 == 0 ? pixelOffsets.left : pixelOffsets.right,
              },
            }"
          >
            <div :class="index % 2 == 0 ? 'even' : 'odd'">
              <p class="location">{{ marker?.title }}</p>
              <a :href="marker?.url?.url" target="_blank" class="title c-mango">{{
                $t(marker?.url?.title?.toLowerCase())
              }}</a>
            </div>
          </GMapInfoWindow>
        </GMapMarker>
      </template>
    </GMapMap>
  </section>
</template>

<style lang="scss" src="./styles/googleMap.scss" />
